/**
 * Collection Filters with Client-Side Filtering
 * 
 * Filtering Logic:
 * - OR logic within same filter group (e.g., Delta OR Brizo)
 * - AND logic between different filter groups (e.g., Brand AND Category)
 * 
 * Brand filter uses product tags (data-tags attribute)
 * Category/Planogram filters navigate to different collections
 * 
 * URL State:
 * - Brand filters are stored in URL params (?brand=delta,brizo)
 * - Filters persist on page refresh and can be shared
 */

class CollectionFilters {
  constructor() {
    this.section = document.getElementById('CollectionProductGrid');
    if (!this.section) return;
    
    this.form = document.getElementById('FacetFiltersForm');
    this.productGrid = document.getElementById('product-grid');
    this.productGridWrapper = document.getElementById('product-grid-wrapper');
    this.productsCount = document.getElementById('products-count');
    this.visibleCount = document.getElementById('visible-count');
    this.activeFilters = document.getElementById('active-filters');
    this.clearAllBtn = document.getElementById('clear-all-filters');
    this.sortSelect = document.getElementById('collection-sort');
    this.searchInput = document.getElementById('collection-search');
    this.searchResults = document.getElementById('search-results');
    this.searchClearBtn = document.getElementById('search-clear');
    this.noResultsHidden = document.getElementById('no-results-hidden');
    this.jsClearAll = document.getElementById('js-clear-all');
    
    this.isLoading = false;
    this.searchTimeout = null;
    this.allProducts = [];
    this.totalProductCount = 0;
    
    this.init();
  }

  init() {
    // Cache all products for client-side filtering
    this.cacheAllProducts();
    if (this.visibleCount) {
      this.visibleCount.textContent = this.totalProductCount;
    }
    this.updateTotalCount();
    
    // Restore filters from URL on page load
    this.restoreFiltersFromUrl();
    
    // Filter checkbox change handlers
    this.form?.querySelectorAll('[data-filter-checkbox]').forEach(checkbox => {
      checkbox.addEventListener('change', (e) => this.handleFilterChange(e));
    });
    
    // Collapsible filter groups
    this.form?.querySelectorAll('.collapsible').forEach(button => {
      button.addEventListener('click', (e) => this.toggleFilterGroup(e.target));
    });
    
    // Restore filter group expand/collapse states from previous page
    this.restoreFilterGroupStates();
    
    // Clear all button
    if (this.clearAllBtn) {
      this.clearAllBtn.addEventListener('click', () => this.clearAllFilters());
    }
    
    // JS clear all button (in no results)
    if (this.jsClearAll) {
      this.jsClearAll.addEventListener('click', () => this.clearAllFilters());
    }
    
    // Sort handler
    if (this.sortSelect) {
      this.sortSelect.addEventListener('change', () => this.handleSortChange());
    }
    
    // Search handlers
    this.setupSearch();
    
    // Infinite scroll
    this.setupInfiniteScroll();
    
    // Show current collection in active filters if not on "all"
    this.showCurrentCollectionFilter();
  }

  /**
   * Cache all product cards for client-side filtering
   */
  cacheAllProducts() {
    this.allProducts = [];
    const productCards = this.productGridWrapper?.querySelectorAll('.product-card');
    
    productCards?.forEach(card => {
      const tagsAttr = card.dataset.tags || '';
      const vendorAttr = card.dataset.vendor || '';
      
      // Parse tags - split by comma, normalize to lowercase
      const tags = tagsAttr.split(',').map(t => t.trim().toLowerCase()).filter(t => t);
      
      this.allProducts.push({
        element: card,
        tags: tags,
        vendor: vendorAttr.toLowerCase(),
        visible: true
      });
    });
    
    this.totalProductCount = this.allProducts.length;
  }

  /**
   * Restore filters from URL parameters on page load
   */
  restoreFiltersFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    
    // Restore brand filters
    const brandParam = urlParams.get('brand');
    if (brandParam) {
      const brands = brandParam.split(',').map(b => b.trim().toLowerCase());
      brands.forEach(brand => {
        const checkbox = this.form?.querySelector(`[data-filter-group="brand"][value="${brand}"]`);
        if (checkbox) {
          checkbox.checked = true;
        }
      });
    }
    
    // Restore availability filters
    const availParam = urlParams.get('availability');
    if (availParam) {
      const availabilities = availParam.split(',').map(a => a.trim().toLowerCase());
      availabilities.forEach(avail => {
        const checkbox = this.form?.querySelector(`[data-filter-group="availability"][value="${avail}"]`);
        if (checkbox) {
          checkbox.checked = true;
        }
      });
    }
    
    // Restore tag filters (like Acrylics)
    const tagsParam = urlParams.get('tags');
    if (tagsParam) {
      const tags = tagsParam.split(',').map(t => t.trim().toLowerCase());
      tags.forEach(tag => {
        const checkbox = this.form?.querySelector(`[data-filter-group="tags"][value="${tag}"]`);
        if (checkbox) {
          checkbox.checked = true;
        }
      });
    }
    
    // Apply restored filters
    const hasFilters = brandParam || availParam || tagsParam;
    if (hasFilters) {
      this.applyClientSideFilters();
    }
  }

  /**
   * Update URL with current filter state (without page reload)
   */
  updateUrlWithFilters() {
    const url = new URL(window.location.href);
    
    // Get brand filters
    const brandFilters = [];
    this.form?.querySelectorAll('[data-filter-group="brand"]:checked').forEach(cb => {
      brandFilters.push(cb.value);
    });
    
    // Get availability filters
    const availFilters = [];
    this.form?.querySelectorAll('[data-filter-group="availability"]:checked').forEach(cb => {
      availFilters.push(cb.value);
    });
    
    // Get tag filters (like Acrylics)
    const tagFilters = [];
    this.form?.querySelectorAll('[data-filter-group="tags"]:checked').forEach(cb => {
      tagFilters.push(cb.value);
    });
    
    // Update URL params
    if (brandFilters.length > 0) {
      url.searchParams.set('brand', brandFilters.join(','));
    } else {
      url.searchParams.delete('brand');
    }
    
    if (availFilters.length > 0) {
      url.searchParams.set('availability', availFilters.join(','));
    } else {
      url.searchParams.delete('availability');
    }
    
    if (tagFilters.length > 0) {
      url.searchParams.set('tags', tagFilters.join(','));
    } else {
      url.searchParams.delete('tags');
    }
    
    // Update URL without reload
    history.replaceState({}, '', url.toString());
  }

  /**
   * Show current collection/tags as active filter tags
   */
  showCurrentCollectionFilter() {
    const productGrid = document.getElementById('product-grid');
    const collectionHandle = productGrid?.dataset.collectionHandle;
    
    // Show collection name if not on "all"
    if (collectionHandle && collectionHandle !== 'all') {
      const matchingCheckbox = this.form?.querySelector(`[data-collection-url][value="${collectionHandle}"]:checked`);
      let collectionTitle = collectionHandle.charAt(0).toUpperCase() + collectionHandle.slice(1);
      const label = matchingCheckbox?.nextElementSibling;
      if (label) {
        collectionTitle = label.textContent.trim();
      }
      this.addCollectionFilterTag(collectionTitle, collectionHandle);
    }
    
    // Show active native collection filters
    this.form?.querySelectorAll('[data-filter-group="native-collection"]:checked').forEach(cb => {
      const label = cb.nextElementSibling;
      const title = label ? label.textContent.trim().replace(/\s*\(\d+\)$/, '') : cb.value;
      this.addNativeCollectionFilterTag(title, cb.value);
    });
  }

  /**
   * Add a collection filter tag to the active filters
   */
  addCollectionFilterTag(title, handle) {
    if (!this.activeFilters) return;
    
    const tag = document.createElement('div');
    tag.className = 'filter-tag';
    tag.dataset.collectionFilter = handle;
    tag.innerHTML = `
      ${title}
      <button type="button" class="filter-tag-remove" data-collection="${handle}" aria-label="Remove ${title} filter">&times;</button>
    `;
    
    // Add remove handler - navigate to all collection
    tag.querySelector('.filter-tag-remove').addEventListener('click', () => {
      window.location.href = '/collections/all';
    });
    
    this.activeFilters.appendChild(tag);
    
    // Show clear all button
    if (this.clearAllBtn) {
      this.clearAllBtn.style.display = 'inline-block';
    }
  }

  /**
   * Add an active tag filter tag to the active filters (with multi-tag removal support)
   */
  addActiveTagFilterTag(title, tagHandle, collectionHandle, allActiveTags) {
    if (!this.activeFilters) return;
    
    const tag = document.createElement('div');
    tag.className = 'filter-tag';
    tag.dataset.tagFilter = tagHandle;
    tag.innerHTML = `
      ${title}
      <button type="button" class="filter-tag-remove" aria-label="Remove ${title} filter">&times;</button>
    `;
    
    // Remove handler - remove just this tag, keep others
    tag.querySelector('.filter-tag-remove').addEventListener('click', () => {
      const remainingTags = allActiveTags.filter(t => t !== tagHandle);
      let newPath;
      if (remainingTags.length > 0) {
        newPath = `/collections/${collectionHandle}/${remainingTags.join('+')}`;
      } else {
        newPath = `/collections/${collectionHandle}`;
      }
      const url = new URL(newPath, window.location.origin);
      const currentParams = new URLSearchParams(window.location.search);
      if (currentParams.get('sort_by')) {
        url.searchParams.set('sort_by', currentParams.get('sort_by'));
      }
      window.location.href = url.toString();
    });
    
    this.activeFilters.appendChild(tag);
    
    if (this.clearAllBtn) {
      this.clearAllBtn.style.display = 'inline-block';
    }
  }

  /**
   * Add a native collection filter tag to the active filters
   */
  addNativeCollectionFilterTag(title, value) {
    if (!this.activeFilters) return;

    const tag = document.createElement('div');
    tag.className = 'filter-tag';
    tag.dataset.nativeCollectionFilter = value;
    tag.innerHTML = `
      ${title}
      <button type="button" class="filter-tag-remove" aria-label="Remove ${title} filter">&times;</button>
    `;

    tag.querySelector('.filter-tag-remove').addEventListener('click', () => {
      const checkbox = this.form?.querySelector(`[data-filter-group="native-collection"][value="${value}"]`);
      if (checkbox) checkbox.checked = false;
      this.handleNativeCollectionFilter();
    });

    this.activeFilters.appendChild(tag);
    if (this.clearAllBtn) this.clearAllBtn.style.display = 'inline-block';
  }

  /**
   * Handle filter checkbox change
   */
  handleFilterChange(event) {
    const checkbox = event.target;
    const filterGroup = checkbox.dataset.filterGroup;
    const filterType = checkbox.dataset.filterType;
    const collectionUrl = checkbox.dataset.collectionUrl;
    
    // Native collection filter — multi-select via Shopify query params
    if (filterGroup === 'native-collection') {
      this.handleNativeCollectionFilter();
      return;
    }

    // For category, planogram filters - navigate to collection (single select)
    if (collectionUrl) {
      if (checkbox.checked) {
        // Preserve current brand/availability filters in URL
        const url = new URL(collectionUrl, window.location.origin);
        const currentParams = new URLSearchParams(window.location.search);
        
        // Carry over brand and availability filters
        if (currentParams.get('brand')) {
          url.searchParams.set('brand', currentParams.get('brand'));
        }
        if (currentParams.get('availability')) {
          url.searchParams.set('availability', currentParams.get('availability'));
        }
        if (currentParams.get('sort_by')) {
          url.searchParams.set('sort_by', currentParams.get('sort_by'));
        }
        
        window.location.href = url.toString();
        return;
      } else {
        // Unchecked - navigate back appropriately
        const currentPath = window.location.pathname;
        const pathParts = currentPath.split('/').filter(p => p);
        
        // Check if this is a tag-based filter (collectionUrl has /tag appended like /collections/all/delta)
        // or a collection-based filter (collectionUrl is just /collections/kitchen)
        const collectionUrlParts = collectionUrl.split('/').filter(p => p);
        const isTagBasedFilter = collectionUrlParts.length === 3; // collections/handle/tag
        
        let baseCollectionUrl;
        if (isTagBasedFilter) {
          // Tag-based filter (Acrylics, Delta, etc.) - go to base collection without tag
          // If current URL is /collections/all/delta, go to /collections/all
          if (pathParts[0] === 'collections' && pathParts[1]) {
            baseCollectionUrl = `/collections/${pathParts[1]}`;
          } else {
            baseCollectionUrl = '/collections/all';
          }
        } else {
          // Collection-based filter (Kitchen, Bath) - always go back to /collections/all
          baseCollectionUrl = '/collections/all';
        }
        
        const url = new URL(baseCollectionUrl, window.location.origin);
        const currentParams = new URLSearchParams(window.location.search);
        
        // Carry over sort
        if (currentParams.get('sort_by')) {
          url.searchParams.set('sort_by', currentParams.get('sort_by'));
        }
        
        window.location.href = url.toString();
        return;
      }
    }
    
    // For brand/availability/tags filters (tag-based) - filter client-side with OR logic
    if (filterType === 'tag' || filterGroup === 'brand' || filterGroup === 'availability' || filterGroup === 'tags') {
      this.applyClientSideFilters();
      this.updateUrlWithFilters();
    }
  }

  /**
   * Handle native collection filter with multi-select support
   * Builds URLs like ?filter.p.m.custom.collection=A&filter.p.m.custom.collection=B
   */
  handleNativeCollectionFilter() {
    const url = new URL(window.location.pathname, window.location.origin);
    const currentParams = new URLSearchParams(window.location.search);

    // Carry over non-filter params
    if (currentParams.get('sort_by')) url.searchParams.set('sort_by', currentParams.get('sort_by'));
    if (currentParams.get('brand')) url.searchParams.set('brand', currentParams.get('brand'));
    if (currentParams.get('availability')) url.searchParams.set('availability', currentParams.get('availability'));

    // Add all checked native collection values
    this.form?.querySelectorAll('[data-filter-group="native-collection"]:checked').forEach(cb => {
      url.searchParams.append(cb.name, cb.value);
    });

    window.location.href = url.toString();
  }

  /**
   * Apply client-side filtering with OR logic within groups, AND between groups
   */
  applyClientSideFilters() {
    // Get all checked filters grouped by filter group
    const filtersByGroup = {};
    
    this.form?.querySelectorAll('[data-filter-checkbox]:checked').forEach(checkbox => {
      const group = checkbox.dataset.filterGroup;
      const value = checkbox.value.toLowerCase();
      const filterType = checkbox.dataset.filterType;
      const collectionUrl = checkbox.dataset.collectionUrl;
      
      // Only handle tag-based filters client-side (not collection-based)
      if ((filterType === 'tag' || group === 'brand' || group === 'availability' || group === 'tags') && !collectionUrl) {
        if (!filtersByGroup[group]) {
          filtersByGroup[group] = [];
        }
        filtersByGroup[group].push(value);
      }
    });
    
    const hasActiveClientFilters = Object.keys(filtersByGroup).length > 0;
    
    // Check if we have collection-based filters active (current collection)
    const productGrid = document.getElementById('product-grid');
    const collectionHandle = productGrid?.dataset.collectionHandle;
    const hasCollectionFilter = collectionHandle && collectionHandle !== 'all';
    
    // Show/hide clear all button
    if (this.clearAllBtn) {
      this.clearAllBtn.style.display = (hasActiveClientFilters || hasCollectionFilter) ? 'inline-block' : 'none';
    }
    
    // Update active filter tags display
    this.updateActiveFilterTags(filtersByGroup);
    
    let visibleCount = 0;
    
    this.allProducts.forEach(product => {
      let shouldShow = true;
      
      // For each filter group (AND between groups)
      for (const [group, values] of Object.entries(filtersByGroup)) {
        // Check if product matches ANY value in this group (OR within group)
        let matchesGroup = false;

        for (const value of values) {
          // Check if any tag exactly matches the filter value
          const tagMatch = product.tags.some(tag => {
            // Exact match comparison (case-insensitive, trimmed)
            return tag.toLowerCase().trim() === value.toLowerCase().trim();
          });
          
          // Also check vendor as fallback for brand
          const vendorMatch = group === 'brand' && product.vendor.includes(value);
          
          if (tagMatch || vendorMatch) {
            matchesGroup = true;
            break;
          }
        }
        
        if (!matchesGroup) {
          shouldShow = false;
          break;
        }
      }
      
      // Show or hide the product card
      product.element.style.display = shouldShow ? '' : 'none';
      product.visible = shouldShow;
      
      if (shouldShow) visibleCount++;
    });
    
    // Update visible count
    if (this.visibleCount) {
      this.visibleCount.textContent = visibleCount;
    }
    
    // Show no results message if needed
    if (visibleCount === 0 && this.allProducts.length > 0) {
      if (this.noResultsHidden) {
        this.noResultsHidden.style.display = 'block';
      }
      if (this.productGridWrapper) {
        this.productGridWrapper.style.display = 'none';
      }
    } else {
      if (this.noResultsHidden) {
        this.noResultsHidden.style.display = 'none';
      }
      if (this.productGridWrapper) {
        this.productGridWrapper.style.display = '';
      }
    }
  }

  /**
   * Update the active filter tags display
   */
  updateActiveFilterTags(filtersByGroup) {
    if (!this.activeFilters) return;
    
    // Remove only client-side filter tags (keep collection and native collection tags)
    this.activeFilters.querySelectorAll('.filter-tag:not([data-collection-filter]):not([data-native-collection-filter])').forEach(tag => {
      tag.remove();
    });
    
    for (const [group, values] of Object.entries(filtersByGroup)) {
      values.forEach(value => {
        const tag = document.createElement('div');
        tag.className = 'filter-tag';
        tag.innerHTML = `
          ${this.formatFilterValue(value)}
          <button type="button" class="filter-tag-remove" data-group="${group}" data-value="${value}" aria-label="Remove ${value} filter">&times;</button>
        `;
        
        // Add remove handler
        tag.querySelector('.filter-tag-remove').addEventListener('click', (e) => {
          const g = e.target.dataset.group;
          const v = e.target.dataset.value;
          
          // Uncheck the corresponding checkbox
          this.form?.querySelectorAll(`[data-filter-group="${g}"]`).forEach(checkbox => {
            if (checkbox.value.toLowerCase() === v) {
              checkbox.checked = false;
            }
          });
          
          // Re-apply filters and update URL
          this.applyClientSideFilters();
          this.updateUrlWithFilters();
        });
        
        this.activeFilters.appendChild(tag);
      });
    }
  }

  /**
   * Capitalize first letter
   */
  capitalizeFirst(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  /**
   * Format a hyphenated filter value into a readable label
   * e.g. "quick-ship" -> "Quick Ship", "made-to-order" -> "Made to Order"
   */
  formatFilterValue(value) {
    const minorWords = ['to', 'of', 'and', 'the', 'or'];
    return value
      .split('-')
      .map((word, index) => {
        if (index > 0 && minorWords.includes(word)) return word;
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join(' ');
  }

  /**
   * Clear all filters
   */
  clearAllFilters() {
    const productGrid = document.getElementById('product-grid');
    const collectionHandle = productGrid?.dataset.collectionHandle;
    const hasNativeFilters = this.form?.querySelector('[data-filter-group="native-collection"]:checked');
    // A tag in the path means a path-based filter is active, e.g. /collections/all/delta
    const hasTagInPath = window.location.pathname.split('/').filter(p => p).length > 2;

    // Uncheck all client-side checkboxes
    this.form?.querySelectorAll('[data-filter-checkbox]').forEach(checkbox => {
      if (!checkbox.dataset.collectionUrl) {
        checkbox.checked = false;
      }
    });

    // If any collection/path/native filter is active, reset to the base "all"
    // collection so every filter (Quick Ship, Category, Brand, etc.) is cleared.
    if (hasNativeFilters || hasTagInPath || (collectionHandle && collectionHandle !== 'all')) {
      const url = new URL('/collections/all', window.location.origin);
      const currentParams = new URLSearchParams(window.location.search);
      if (currentParams.get('sort_by')) {
        url.searchParams.set('sort_by', currentParams.get('sort_by'));
      }
      window.location.href = url.toString();
      return;
    }

    this.applyClientSideFilters();
    this.updateUrlWithFilters();
  }

  /**
   * Handle sort change - preserves filter state
   */
  handleSortChange() {
    if (!this.sortSelect) return;
    
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', this.sortSelect.value);
    
    // URL already has brand/availability params preserved
    window.location.href = url.toString();
  }

  /**
   * Toggle collapsible filter group
   */
  toggleFilterGroup(button) {
    const isCollapsed = button.classList.contains('collapsed');
    const filterGroup = button.closest('.filter-group');
    const filterOptions = filterGroup?.querySelector('.filter-options');
    const groupName = filterGroup?.dataset.filterGroup;
    
    if (isCollapsed) {
      button.classList.remove('collapsed');
      button.setAttribute('aria-expanded', 'true');
      filterOptions?.classList.remove('collapsed');
    } else {
      button.classList.add('collapsed');
      button.setAttribute('aria-expanded', 'false');
      filterOptions?.classList.add('collapsed');
    }
    
    // Persist expand/collapse state in sessionStorage
    if (groupName) {
      try {
        const states = JSON.parse(sessionStorage.getItem('filterGroupStates') || '{}');
        states[groupName] = !isCollapsed ? 'collapsed' : 'expanded';
        sessionStorage.setItem('filterGroupStates', JSON.stringify(states));
      } catch (e) { /* ignore storage errors */ }
    }
  }

  /**
   * Restore filter group expand/collapse states from sessionStorage
   */
  restoreFilterGroupStates() {
    try {
      const states = JSON.parse(sessionStorage.getItem('filterGroupStates') || '{}');
      
      for (const [groupName, state] of Object.entries(states)) {
        const filterGroup = this.form?.querySelector(`.filter-group[data-filter-group="${groupName}"]`);
        if (!filterGroup) continue;
        
        const button = filterGroup.querySelector('.collapsible');
        const filterOptions = filterGroup.querySelector('.filter-options');
        if (!button || !filterOptions) continue;
        
        if (state === 'expanded') {
          button.classList.remove('collapsed');
          button.setAttribute('aria-expanded', 'true');
          filterOptions.classList.remove('collapsed');
        } else if (state === 'collapsed') {
          button.classList.add('collapsed');
          button.setAttribute('aria-expanded', 'false');
          filterOptions.classList.add('collapsed');
        }
      }
    } catch (e) { /* ignore storage errors */ }
  }

  /**
   * Setup infinite scroll
   */
  setupInfiniteScroll() {
    const loadingIndicator = this.productGrid?.querySelector('.infinite-scroll-loading');
    if (!loadingIndicator) return;
    
    // Disconnect existing observer if any
    if (this.scrollObserver) {
      this.scrollObserver.disconnect();
    }
    
    this.scrollObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.isLoading) {
          this.loadMoreProducts();
        }
      });
    }, { rootMargin: '200px' });
    
    this.scrollObserver.observe(loadingIndicator);
  }

  /**
   * Load more products for infinite scroll
   */
  loadMoreProducts() {
    const loadingIndicator = this.productGrid?.querySelector('.infinite-scroll-loading');
    if (!loadingIndicator) return;
    
    const nextUrl = loadingIndicator.dataset.nextUrl;
    if (!nextUrl || this.isLoading) return;
    
    this.isLoading = true;
    const spinner = loadingIndicator.querySelector('.loading-spinner');
    if (spinner) spinner.textContent = 'Loading...';
    
    fetch(nextUrl)
      .then(response => response.text())
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newProducts = doc.querySelector('#product-grid-wrapper');
        
        if (newProducts && this.productGridWrapper) {
          // Append new products
          Array.from(newProducts.children).forEach(product => {
            const clonedProduct = product.cloneNode(true);
            this.productGridWrapper.appendChild(clonedProduct);
            
            // Add to cached products
            const tagsAttr = clonedProduct.dataset?.tags || '';
            const vendorAttr = clonedProduct.dataset?.vendor || '';
            const tags = tagsAttr.split(',').map(t => t.trim().toLowerCase()).filter(t => t);
            
            this.allProducts.push({
              element: clonedProduct,
              tags: tags,
              vendor: vendorAttr.toLowerCase(),
              visible: true
            });
          });
          
          // Update total count
          this.totalProductCount = this.allProducts.length;
          this.updateTotalCount();
          
          // Re-apply current filters to new products
          const hasActiveFilters = this.form?.querySelector('[data-filter-group="brand"]:checked, [data-filter-group="availability"]:checked, [data-filter-group="tags"]:checked, [data-filter-group="native-collection"]:checked');
          if (hasActiveFilters) {
            this.applyClientSideFilters();
          } else {
            // Just update visible count
            if (this.visibleCount) {
              this.visibleCount.textContent = this.allProducts.length;
            }
          }
          
          // Update next URL or remove loading indicator
          const nextLoadingIndicator = doc.querySelector('.infinite-scroll-loading');
          if (nextLoadingIndicator && nextLoadingIndicator.dataset.nextUrl) {
            loadingIndicator.dataset.nextUrl = nextLoadingIndicator.dataset.nextUrl;
            if (spinner) spinner.textContent = 'Loading more products...';
          } else {
            loadingIndicator.remove();
          }
        }
        
        this.isLoading = false;
      })
      .catch(error => {
        console.error('Error loading more products:', error);
        if (spinner) spinner.textContent = 'Error loading products';
        this.isLoading = false;
      });
  }

  /**
   * Update total product count display
   */
  updateTotalCount() {
    if (!this.productsCount) return;
    
    // Update the total count (the number after the slash)
    const countText = this.productsCount.innerHTML;
    const match = countText.match(/\/\s*(\d+)\s*Results/);
    if (match) {
      this.productsCount.innerHTML = countText.replace(/\/\s*\d+\s*Results/, `/ ${this.totalProductCount} Results`);
    }
  }

  /**
   * Setup search functionality
   */
  setupSearch() {
    if (!this.searchInput) return;

    this.searchInput.addEventListener('input', (e) => {
      const query = e.target.value.trim();

      if (this.searchClearBtn) {
        this.searchClearBtn.style.display = query ? 'flex' : 'none';
      }

      clearTimeout(this.searchTimeout);
      if (query.length >= 2) {
        this.searchTimeout = setTimeout(() => this.performSearch(query), 300);
      } else {
        this.hideSearchResults();
      }
    });

    if (this.searchClearBtn) {
      this.searchClearBtn.addEventListener('click', () => {
        this.searchInput.value = '';
        this.searchClearBtn.style.display = 'none';
        this.hideSearchResults();
      });
    }

    document.addEventListener('click', (e) => {
      if (!this.searchInput.contains(e.target) && !this.searchResults?.contains(e.target)) {
        this.hideSearchResults();
      }
    });

    this.searchInput.addEventListener('focus', () => {
      if (this.searchInput.value.trim() && this.searchResults?.querySelector('.search-result-item')) {
        this.searchResults.style.display = 'block';
      }
    });
  }

  async filterUnavailableNoChargeResults(results) {
    const checked = await Promise.all(results.map(async (result) => {
      const tags = result.product?.tags || [];
      const isNoCharge = tags.some(
        tag => String(tag).toLowerCase() === 'display-no charge'
      );
      if (!isNoCharge || !result.product?.handle) return result;

      try {
        const response = await fetch(`/products/${encodeURIComponent(result.product.handle)}.js`, {
          credentials: 'same-origin'
        });
        if (response.status === 404) return null;
        if (!response.ok) return result;

        const storefrontProduct = await response.json();
        const variants = storefrontProduct.variants || [];
        const matchedSku = result.primarySKU || '';
        const matchedVariant = variants.find(variant => variant.sku === matchedSku);
        const matchedIsDsp = /-DSP(?:-|\/|$)/i.test(matchedSku);
        const isVisible = matchedIsDsp && matchedVariant
          ? matchedVariant.available
          : variants.some(variant => variant.available);

        if (!isVisible) return null;

        result.productUrl = `/products/${result.product.handle}`;
        if (matchedVariant) {
          result.matchedVariant.id = matchedVariant.id;
          result.productUrl += `?variant=${matchedVariant.id}`;
        }
        return result;
      } catch (error) {
        console.warn('Could not verify Quick Ship availability:', error);
        return result;
      }
    }));

    return checked.filter(Boolean);
  }

  async performSearch(query) {
    try {
      const apiUrl = `https://phpstack-1318127-5961230.cloudwaysapps.com/api/products/search?q=${encodeURIComponent(query)}`;
      
      const response = await fetch(apiUrl);
      if (!response.ok) throw new Error('Search API request failed');
      
      const data = await response.json();
      
      const results = data.results
        .filter(item => item.url && item.handle)
        .map(item => ({
          product: {
            id: item.id,
            title: item.title,
            handle: item.handle,
            vendor: item.vendor,
            tags: item.tags,
            image: item.image
          },
          matchedVariant: item.matched_variant ? {
            id: item.matched_variant.id,
            title: item.matched_variant.title,
            sku: item.matched_variant.sku,
            image: item.matched_variant.image
          } : null,
          primarySKU: item.matched_variant?.sku || '',
          deposcoId: item.matched_variant?.deposco_id || '',
          productUrl: item.url
        }));
      
      const visibleResults = await this.filterUnavailableNoChargeResults(results.slice(0, 50));
      this.displaySearchResults(visibleResults, query);
      
    } catch (error) {
      console.error('Search API error:', error);
      this.displaySearchResults([], query);
    }
  }

  displaySearchResults(results, query) {
    const resultsList = this.searchResults?.querySelector('.search-results-list');
    const resultsCount = this.searchResults?.querySelector('.search-results-count');
    if (!resultsList || !resultsCount) return;

    if (results.length === 0) {
      resultsList.innerHTML = `
        <div class="search-no-results">
          <svg class="search-no-results-icon" viewBox="0 0 48 48" fill="none">
            <circle cx="24" cy="24" r="20" stroke="currentColor" stroke-width="2"/>
            <path d="M24 16v12M24 32h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
          No products found for "${query}"
        </div>
      `;
      resultsCount.textContent = 'No Results';
    } else {
      resultsCount.textContent = `${results.length} Result${results.length !== 1 ? 's' : ''}`;
      
      resultsList.innerHTML = results.map(result => {
        const product = result.product;
        // Use variant image if available, otherwise fall back to product image
        const imageUrl = result.matchedVariant?.image || product.image || '';
        const isQuickShip = product.tags?.some(tag => {
          const normalized = tag.toLowerCase();
          return normalized.includes('quick ship') || normalized === 'display-no charge';
        });
        
        let productUrl = result.productUrl || `/products/${product.handle}`;
        
        if (result.matchedVariant?.id && !productUrl.includes('variant=')) {
          const separator = productUrl.includes('?') ? '&' : '?';
          productUrl = `${productUrl}${separator}variant=${result.matchedVariant.id}`;
        }

        return `
          <a href="${productUrl}" class="search-result-item" data-deposco="${result.deposcoId || ''}">
            ${imageUrl ? `<img src="${imageUrl}" alt="${product.title}" class="search-result-image" loading="lazy">` : '<div class="search-result-image"></div>'}
            <div class="search-result-info">
              ${product.vendor ? `<div class="search-result-brand">${product.vendor}</div>` : ''}
              <div class="search-result-title">${product.title}</div>
              <div class="search-result-meta">
                ${result.primarySKU ? `<span class="search-result-sku">SKU: ${result.primarySKU}</span>` : ''}
                ${result.deposcoId ? `<span class="search-result-deposco">Deposco ID: ${result.deposcoId}</span>` : ''}
                ${isQuickShip ? '<span class="search-result-badge">Quick Ship</span>' : ''}
              </div>
            </div>
          </a>
        `;
      }).join('');
    }

    this.searchResults.style.display = 'block';
  }

  hideSearchResults() {
    if (this.searchResults) {
      this.searchResults.style.display = 'none';
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new CollectionFilters());
} else {
  new CollectionFilters();
}
